import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Alert,
  CircularProgress,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Typography,
  Card,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  FormControlLabel,
  Tooltip,
  Divider,
  Menu,
  ListItemText,
  LinearProgress,
  InputAdornment,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Link as LinkIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  Category as CategoryIcon,
  Store as StoreIcon,
  Inbox as InboxIcon,
  LabelOutlined as LabelIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Language as WebIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import weddingEventService from '../../services/weddingEventService';
import DialogHeader from '../Common/DialogHeader';
import apiService from '../../services/apiService';
import { API_ENDPOINTS } from '../../config/api';

const fmt = (val) =>
  val != null ? `${parseFloat(val).toLocaleString('hu-HU')} Ft` : '—';

const STATUS_COLORS = {
  RESEARCHING: 'default',
  CONTACTED: 'info',
  QUOTE_RECEIVED: 'warning',
  BOOKED: 'success',
  CANCELLED: 'error',
};

const STATUS_LABELS = {
  RESEARCHING: 'Kutatás',
  CONTACTED: 'Kapcsolatba lépve',
  QUOTE_RECEIVED: 'Ajánlat beérkezett',
  BOOKED: 'Lefoglalva',
  CANCELLED: 'Lemondva',
};

const EMPTY_FORM = {
  vendorCategoryId: '',
  name: '',
  contactPerson: '',
  email: '',
  phone: '',
  website: '',
  estimatedCost: '',
  actualCost: '',
  notes: '',
};

// ── Budget progressbar for category header ───────────────────
// Csak akkor jelenik meg, ha a kategóriának van megadott tervezett kerete.
function CategoryBudgetBar({ category, vendors }) {
  const tervezett = category?.plannedBudget ? Number(category.plannedBudget) : null;
  const valos     = vendors.reduce((s, v) => s + (v.actualCost || 0), 0);

  if (tervezett == null) return null;

  const egyenleg = valos - tervezett;
  const pct      = tervezett > 0 ? Math.min((valos / tervezett) * 100, 100) : 0;
  const over     = tervezett > 0 && valos > tervezett;

  return (
    <Box sx={{ mt: 0.75, width: '100%' }} onClick={(e) => e.stopPropagation()}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Typography variant="caption" color="text.secondary">
            Keret: <strong>{fmt(tervezett)}</strong>
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Valós: <strong>{fmt(valos)}</strong>
          </Typography>
        </Box>
        <Typography
          variant="caption"
          fontWeight={700}
          sx={{ color: egyenleg <= 0 ? 'success.main' : 'error.main' }}
        >
          {egyenleg > 0 ? `+${fmt(egyenleg)} túllépés` : egyenleg < 0 ? `${fmt(Math.abs(egyenleg))} szabad keret` : 'Egyenlő'}
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={pct}
        sx={{
          height: 6,
          borderRadius: 3,
          bgcolor: 'grey.200',
          '& .MuiLinearProgress-bar': {
            bgcolor: over ? 'error.main' : valos > 0 ? 'success.main' : 'grey.400',
            borderRadius: 3,
          },
        }}
      />
    </Box>
  );
}

// ── Category assign menu (shared for custom + marketplace) ────
function CategoryAssignMenu({ vendor, vendorCategories, onAssignCategory, touch = false }) {
  const [anchor, setAnchor] = useState(null);

  const handleOpen = (e) => { e.stopPropagation(); setAnchor(e.currentTarget); };
  const handleSelect = (catId) => { setAnchor(null); onAssignCategory(vendor, catId); };

  return (
    <>
      <Tooltip title={vendor.vendorCategoryId ? `Kategória: ${vendor.vendorCategoryName}` : 'Kategória hozzárendelése'}>
        <IconButton
          size="small"
          onClick={handleOpen}
          color={vendor.vendorCategoryId ? 'primary' : 'default'}
          aria-label="Kategória hozzárendelése"
          sx={touch ? { width: 40, height: 40 } : undefined}
        >
          <LabelIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        onClick={(e) => e.stopPropagation()}
      >
        <MenuItem onClick={() => handleSelect(null)} selected={!vendor.vendorCategoryId}>
          <ListItemText
            primary="Nincs kategória"
            slotProps={{ primary: { sx: { color: 'text.secondary', fontStyle: 'italic' } } }}
          />
        </MenuItem>
        <Divider />
        {vendorCategories.length === 0 ? (
          <MenuItem disabled>
            <ListItemText
              primary="Nincs még kategória"
              slotProps={{ primary: { sx: { color: 'text.secondary' } } }}
            />
          </MenuItem>
        ) : (
          vendorCategories.map((cat) => (
            <MenuItem
              key={cat.id}
              onClick={() => handleSelect(cat.id)}
              selected={vendor.vendorCategoryId === cat.id}
            >
              <ListItemText primary={cat.name} />
            </MenuItem>
          ))
        )}
      </Menu>
    </>
  );
}

// ── Single vendor row ─────────────────────────────────────────
function VendorRow({ vendor, onEdit, onDelete, onToggleBudget, onAssignCategory, isMarketplace, vendorCategories }) {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const touchSx = isMobile ? { width: 40, height: 40 } : undefined;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: isMobile ? 'flex-start' : 'center',
        gap: 1.5,
        py: 1.75,
        px: 2.5,
        '&:hover': { bgcolor: 'action.hover' },
        flexDirection: isMobile ? 'column' : 'row',
        borderBottom: '1px solid',
        borderColor: 'divider',
        '&:last-child': { borderBottom: 'none' },
      }}
    >
      {/* Checkbox — only for custom vendors; mobilon felirattal a műveletek sorába kerül */}
      {isMobile ? null : !isMarketplace ? (
        <Tooltip title={vendor.includedInBudget ? 'Kivesz a költségvetésből' : 'Beleszámol a költségvetésbe'}>
          <Checkbox
            size="small"
            checked={!!vendor.includedInBudget}
            onChange={() => onToggleBudget(vendor)}
            sx={{ p: 0.5 }}
          />
        </Tooltip>
      ) : (
        <Box sx={{ width: 28 }} />
      )}

      {/* Name + status */}
      {/* Mobilon (oszlopos elrendezés) a teljes szélességet kapja, különben a hosszú név kilóg */}
      <Box sx={{ flex: 1, minWidth: 0, width: isMobile ? '100%' : 'auto' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', minWidth: 0 }}>
          <Typography
            variant="body2"
            fontWeight={600}
            noWrap={!isMobile}
            title={vendor.name}
            sx={{ minWidth: 0, maxWidth: '100%', overflowWrap: 'anywhere' }}
          >
            {vendor.name}
          </Typography>
          <Chip label={STATUS_LABELS[vendor.status] || vendor.status} color={STATUS_COLORS[vendor.status] || 'default'} size="small" />
          {isMarketplace && <Chip label="Piactér" size="small" color="primary" variant="outlined" />}
        </Box>
        {vendor.contactPerson && (
          <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>{vendor.contactPerson}</Typography>
        )}
        {isMobile && (vendor.estimatedCost != null || vendor.actualCost != null) && (
          <Box sx={{ mt: 0.5 }}>
            {vendor.estimatedCost != null && (
              <Typography variant="caption" color="text.secondary">Tervezett: <strong>{fmt(vendor.estimatedCost)}</strong></Typography>
            )}
            {vendor.actualCost != null && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Valós: <strong>{fmt(vendor.actualCost)}</strong></Typography>
            )}
          </Box>
        )}
      </Box>

      {/* Costs — desktop only */}
      {!isMobile && (
        <Box sx={{ textAlign: 'right', minWidth: 160 }}>
          <Typography variant="body2">Tervezett: {fmt(vendor.estimatedCost)}</Typography>
          {vendor.actualCost != null && (
            <Typography variant="caption" color="text.secondary">Valós: {fmt(vendor.actualCost)}</Typography>
          )}
        </Box>
      )}

      {/* Actions — mobilon teljes szélességű sor, 40 px-es érintési felületekkel */}
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0,
        ...(isMobile && { width: '100%', justifyContent: 'space-between', mt: -0.5 }),
      }}>
        {isMobile && (
          !isMarketplace ? (
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!vendor.includedInBudget}
                  onChange={() => onToggleBudget(vendor)}
                />
              }
              label="Költségvetésben"
              slotProps={{ typography: { variant: 'body2', color: 'text.secondary' } }}
              sx={{ ml: -1, mr: 0 }}
            />
          ) : <Box />
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {/* Category assign — for ALL vendor types */}
          <CategoryAssignMenu vendor={vendor} vendorCategories={vendorCategories} onAssignCategory={onAssignCategory} touch={isMobile} />

          {isMarketplace ? (
            <IconButton size="small" onClick={() => navigate('/foglalasok')} title="Foglalás megtekintése" aria-label="Foglalás megtekintése" sx={touchSx}>
              <LinkIcon fontSize="small" />
            </IconButton>
          ) : (
            <>
              <IconButton size="small" onClick={() => onEdit(vendor)} title="Szerkesztés" aria-label="Szerkesztés" sx={touchSx}>
                <EditIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" onClick={() => onDelete(vendor)} color="error" title="Törlés" aria-label="Törlés" sx={touchSx}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
}

// ── Category accordion block ──────────────────────────────────
function CategorySection({
  category, vendors, vendorCategories,
  onAddVendor, onEditVendor, onDeleteVendor,
  onToggleBudget, onAssignCategory,
  onRenameCategory, onDeleteCategory,
}) {
  const [expanded, setExpanded] = useState(true);

  return (
    <Accordion
      expanded={expanded}
      onChange={() => setExpanded((p) => !p)}
      disableGutters
      elevation={0}
      sx={{
        border: (t) => `1px solid ${t.palette.divider}`,
        borderRadius: '10px !important',
        '&:before': { display: 'none' },
        mb: 2.5,
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          bgcolor: 'grey.50',
          borderRadius: expanded ? '10px 10px 0 0' : '10px',
          px: 2.5,
          py: 1,
          minHeight: 64,
          '& .MuiAccordionSummary-content': { my: 0.75, flexDirection: 'column', width: '100%' },
        }}
      >
        {/* Top row: icon, name, actions */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', minWidth: 0 }}>
          <CategoryIcon fontSize="small" color="action" sx={{ flexShrink: 0 }} />
          <Typography variant="subtitle1" fontWeight={700} noWrap sx={{ flex: 1, minWidth: 0 }}>
            {category.name}
          </Typography>
          <Tooltip title="Kategória átnevezése">
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); onRenameCategory(category); }}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Kategória törlése">
            <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); onDeleteCategory(category); }}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Button
            size="small"
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={(e) => { e.stopPropagation(); onAddVendor(category.id); }}
            sx={{ ml: 0.5, flexShrink: 0 }}
          >
            Hozzáad
          </Button>
        </Box>
        {/* Budget progressbar */}
        <CategoryBudgetBar category={category} vendors={vendors} />
      </AccordionSummary>

      <AccordionDetails sx={{ p: 0 }}>
        {vendors.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
            Még nincs szolgáltató ebben a kategóriában
          </Typography>
        ) : (
          <Box>
            {vendors.map((v, i) => (
              <React.Fragment key={v.id}>
                {i > 0 && <Divider />}
                <VendorRow
                  vendor={v}
                  vendorCategories={vendorCategories}
                  onEdit={onEditVendor}
                  onDelete={onDeleteVendor}
                  onToggleBudget={onToggleBudget}
                  onAssignCategory={onAssignCategory}
                  isMarketplace={v.type === 'marketplace'}
                />
              </React.Fragment>
            ))}
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  );
}

// ── Vendor form dialog (add + edit) ──────────────────────────
function VendorFormDialog({ open, title, formData, onChange, onClose, onSubmit, submitLabel, vendorCategories, isMobile }) {
  const set = (field) => (e) => onChange({ ...formData, [field]: e.target.value });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
      <DialogTitle sx={{ p: 0 }}>
        <DialogHeader
          icon={title?.toLowerCase().includes('szerkeszt') ? <EditIcon /> : <StoreIcon />}
          title={title}
          onClose={onClose}
        />
      </DialogTitle>

      <DialogContent sx={{ pt: 0 }}>
        <Stack spacing={2.5} sx={{ mt: 1 }}>

          {/* ── Besorolás ── */}
          <Box>
            <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1, lineHeight: 1 }}>
              Besorolás
            </Typography>
            <FormControl fullWidth size="small">
              <InputLabel>Kategória</InputLabel>
              <Select
                value={formData.vendorCategoryId}
                onChange={set('vendorCategoryId')}
                label="Kategória"
              >
                <MenuItem value=""><em>Nincs kategória</em></MenuItem>
                {vendorCategories.map((cat) => (
                  <MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Divider />

          {/* ── Alapadatok ── */}
          <Box>
            <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1.5, lineHeight: 1 }}>
              Alapadatok
            </Typography>
            <Stack spacing={1.5}>
              <TextField
                label="Szolgáltató neve *"
                size="small"
                fullWidth
                value={formData.name}
                onChange={set('name')}
                placeholder="pl. Fotós Pista"
              />
              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Kapcsolattartó"
                    size="small"
                    fullWidth
                    value={formData.contactPerson}
                    onChange={set('contactPerson')}
                    InputProps={{ startAdornment: <InputAdornment position="start"><PersonIcon fontSize="small" color="action" /></InputAdornment> }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="E-mail cím"
                    type="email"
                    size="small"
                    fullWidth
                    value={formData.email}
                    onChange={set('email')}
                    InputProps={{ startAdornment: <InputAdornment position="start"><EmailIcon fontSize="small" color="action" /></InputAdornment> }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Telefonszám"
                    size="small"
                    fullWidth
                    value={formData.phone}
                    onChange={set('phone')}
                    placeholder="+36 30 123 4567"
                    InputProps={{ startAdornment: <InputAdornment position="start"><PhoneIcon fontSize="small" color="action" /></InputAdornment> }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Weboldal"
                    size="small"
                    fullWidth
                    value={formData.website}
                    onChange={set('website')}
                    placeholder="https://..."
                    InputProps={{ startAdornment: <InputAdornment position="start"><WebIcon fontSize="small" color="action" /></InputAdornment> }}
                  />
                </Grid>
              </Grid>
            </Stack>
          </Box>

          <Divider />

          {/* ── Pénzügy ── */}
          <Box>
            <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1.5, lineHeight: 1 }}>
              Pénzügy
            </Typography>
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Tervezett költség"
                  type="number"
                  size="small"
                  fullWidth
                  value={formData.estimatedCost}
                  onChange={set('estimatedCost')}
                  slotProps={{ input: { endAdornment: <InputAdornment position="end">Ft</InputAdornment> } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Tényleges költség"
                  type="number"
                  size="small"
                  fullWidth
                  value={formData.actualCost}
                  onChange={set('actualCost')}
                  slotProps={{ input: { endAdornment: <InputAdornment position="end">Ft</InputAdornment> } }}
                />
              </Grid>
            </Grid>
            {/* Live egyenleg preview */}
            {(formData.estimatedCost || formData.actualCost) && (
              <Box sx={{ mt: 1, p: 1.25, bgcolor: 'grey.50', borderRadius: 1 }}>
                {(() => {
                  const terv = parseFloat(formData.estimatedCost) || 0;
                  const val  = parseFloat(formData.actualCost)    || 0;
                  const diff = val - terv;
                  const pct  = terv > 0 ? Math.min((val / terv) * 100, 100) : 0;
                  return (
                    <>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">
                          {terv > 0 ? `${Math.round((val / terv) * 100)}%` : '—'}
                        </Typography>
                        <Typography
                          variant="caption"
                          fontWeight={700}
                          sx={{ color: diff <= 0 ? 'success.main' : 'error.main' }}
                        >
                          {diff > 0 ? `+${fmt(diff)} túllépés` : diff < 0 ? `${fmt(diff)} megtakarítás` : 'Egyenlő'}
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={pct}
                        sx={{
                          height: 4,
                          borderRadius: 2,
                          bgcolor: 'grey.200',
                          '& .MuiLinearProgress-bar': {
                            bgcolor: diff > 0 ? 'error.main' : val > 0 ? 'success.main' : 'grey.400',
                            borderRadius: 2,
                          },
                        }}
                      />
                    </>
                  );
                })()}
              </Box>
            )}
          </Box>

          <Divider />

          {/* ── Megjegyzések ── */}
          <TextField
            label="Megjegyzések"
            multiline
            rows={2}
            size="small"
            fullWidth
            value={formData.notes}
            onChange={set('notes')}
          />

        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2.5, gap: 1, borderTop: '1px solid', borderTopColor: 'divider' }}>
        <Button onClick={onClose}>Mégse</Button>
        <Button variant="contained" onClick={onSubmit} disabled={!formData.name.trim()}>
          {submitLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── MAIN COMPONENT ────────────────────────────────────────────
function VendorsTab({ event, onRefresh }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [vendors, setVendors] = useState([]);
  const [vendorCategories, setVendorCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [addVendorOpen, setAddVendorOpen] = useState(false);
  const [editVendorOpen, setEditVendorOpen] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [addCategoryOpen, setAddCategoryOpen] = useState(false);
  const [renameCategoryOpen, setRenameCategoryOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryBudget, setNewCategoryBudget] = useState('');
  const [formData, setFormData] = useState(EMPTY_FORM);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [vendorsRes, catsRes] = await Promise.all([
        weddingEventService.getVendors(event.id),
        apiService.get(API_ENDPOINTS.WEDDING_EVENTS.VENDOR_CATEGORIES(event.id)),
      ]);
      if (vendorsRes.success) setVendors(vendorsRes.data);
      if (catsRes.success) setVendorCategories(catsRes.data);
    } catch (err) {
      console.error('Fetch vendors error:', err);
      setError('Hiba történt az adatok betöltése során.');
    } finally {
      setLoading(false);
    }
  }, [event.id]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── Vendor CRUD ──────────────────────────────────────────

  const resetForm = () => setFormData(EMPTY_FORM);

  const handleAddVendor = async () => {
    try {
      await weddingEventService.addCustomVendor(event.id, {
        vendorCategoryId: formData.vendorCategoryId ? parseInt(formData.vendorCategoryId) : null,
        includedInBudget: true,
        name: formData.name,
        contactPerson: formData.contactPerson || null,
        email: formData.email || null,
        phone: formData.phone || null,
        website: formData.website || null,
        estimatedCost: formData.estimatedCost ? parseFloat(formData.estimatedCost) : null,
        actualCost: formData.actualCost ? parseFloat(formData.actualCost) : null,
        notes: formData.notes || null,
        status: 'RESEARCHING',
      });
      setAddVendorOpen(false);
      resetForm();
      fetchData();
      onRefresh();
    } catch (err) {
      console.error('Add vendor error:', err);
      alert('Hiba történt a szolgáltató hozzáadása során.');
    }
  };

  const handleEditVendor = async () => {
    try {
      await weddingEventService.updateCustomVendor(event.id, selectedVendor.customVendorId, {
        vendorCategoryId: formData.vendorCategoryId ? parseInt(formData.vendorCategoryId) : null,
        name: formData.name,
        contactPerson: formData.contactPerson || null,
        email: formData.email || null,
        phone: formData.phone || null,
        website: formData.website || null,
        estimatedCost: formData.estimatedCost ? parseFloat(formData.estimatedCost) : null,
        actualCost: formData.actualCost ? parseFloat(formData.actualCost) : null,
        notes: formData.notes || null,
      });
      setEditVendorOpen(false);
      setSelectedVendor(null);
      resetForm();
      fetchData();
      onRefresh();
    } catch (err) {
      console.error('Edit vendor error:', err);
      alert('Hiba történt a szolgáltató frissítése során.');
    }
  };

  const handleDeleteVendor = async (vendor) => {
    if (!window.confirm(`Biztosan törölni szeretnéd: ${vendor.name}?`)) return;
    try {
      await weddingEventService.deleteCustomVendor(event.id, vendor.customVendorId);
      fetchData();
      onRefresh();
    } catch (err) {
      console.error('Delete vendor error:', err);
      alert('Hiba történt a törlés során.');
    }
  };

  const openEditDialog = (vendor) => {
    setSelectedVendor(vendor);
    setFormData({
      vendorCategoryId: vendor.vendorCategoryId || '',
      name: vendor.name,
      contactPerson: vendor.contactPerson || '',
      email: vendor.email || '',
      phone: vendor.phone || '',
      website: vendor.website || '',
      estimatedCost: vendor.estimatedCost != null ? parseFloat(vendor.estimatedCost) : '',
      actualCost: vendor.actualCost != null ? parseFloat(vendor.actualCost) : '',
      notes: vendor.notes || '',
    });
    setEditVendorOpen(true);
  };

  const handleToggleBudget = async (vendor) => {
    setVendors((prev) =>
      prev.map((v) =>
        v.customVendorId === vendor.customVendorId ? { ...v, includedInBudget: !v.includedInBudget } : v
      )
    );
    try {
      await weddingEventService.updateCustomVendor(event.id, vendor.customVendorId, {
        includedInBudget: !vendor.includedInBudget,
      });
    } catch (err) {
      console.error('Toggle budget error:', err);
      setVendors((prev) =>
        prev.map((v) =>
          v.customVendorId === vendor.customVendorId ? { ...v, includedInBudget: vendor.includedInBudget } : v
        )
      );
    }
  };

  // Works for both custom and marketplace vendors
  const handleAssignCategory = async (vendor, catId) => {
    const newCatId   = catId || null;
    const newCatName = catId ? vendorCategories.find((c) => c.id === catId)?.name || null : null;
    const isCustom   = vendor.type === 'custom';
    const matchKey   = isCustom ? 'customVendorId' : 'budgetItemId';

    setVendors((prev) =>
      prev.map((v) =>
        v[matchKey] === vendor[matchKey]
          ? { ...v, vendorCategoryId: newCatId, vendorCategoryName: newCatName }
          : v
      )
    );
    try {
      if (isCustom) {
        await weddingEventService.updateCustomVendor(event.id, vendor.customVendorId, { vendorCategoryId: newCatId });
      } else {
        await apiService.patch(
          API_ENDPOINTS.WEDDING_EVENTS.ASSIGN_MARKETPLACE_VENDOR_CATEGORY(event.id, vendor.budgetItemId),
          { vendorCategoryId: newCatId }
        );
      }
    } catch (err) {
      console.error('Assign category error:', err);
      setVendors((prev) =>
        prev.map((v) =>
          v[matchKey] === vendor[matchKey]
            ? { ...v, vendorCategoryId: vendor.vendorCategoryId, vendorCategoryName: vendor.vendorCategoryName }
            : v
        )
      );
    }
  };

  // ── Vendor Category CRUD ─────────────────────────────────

  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) return;
    try {
      await apiService.post(API_ENDPOINTS.WEDDING_EVENTS.ADD_VENDOR_CATEGORY(event.id), {
        name: newCategoryName.trim(),
        plannedBudget: newCategoryBudget ? parseFloat(newCategoryBudget) : null,
      });
      setAddCategoryOpen(false);
      setNewCategoryName('');
      setNewCategoryBudget('');
      fetchData();
    } catch (err) {
      console.error('Add category error:', err);
      alert('Hiba történt a kategória hozzáadása során.');
    }
  };

  const handleRenameCategory = async () => {
    if (!newCategoryName.trim() || !selectedCategory) return;
    try {
      await apiService.patch(
        API_ENDPOINTS.WEDDING_EVENTS.UPDATE_VENDOR_CATEGORY(event.id, selectedCategory.id),
        {
          name: newCategoryName.trim(),
          plannedBudget: newCategoryBudget ? parseFloat(newCategoryBudget) : null,
        }
      );
      setRenameCategoryOpen(false);
      setSelectedCategory(null);
      setNewCategoryName('');
      setNewCategoryBudget('');
      fetchData();
    } catch (err) {
      console.error('Rename category error:', err);
      alert('Hiba történt az átnevezés során.');
    }
  };

  const handleDeleteCategory = async (category) => {
    if (!window.confirm(`Biztosan törölni szeretnéd a "${category.name}" kategóriát? A benne lévő szolgáltatók kategória nélkül maradnak.`)) return;
    try {
      await apiService.delete(API_ENDPOINTS.WEDDING_EVENTS.DELETE_VENDOR_CATEGORY(event.id, category.id));
      fetchData();
    } catch (err) {
      console.error('Delete category error:', err);
      alert('Hiba történt a kategória törlése során.');
    }
  };

  // ── Derived data ─────────────────────────────────────────

  const vendorsByCategoryId = {};
  const marketplaceVendors  = [];
  const uncategorizedVendors = [];

  vendors.forEach((v) => {
    if (v.vendorCategoryId) {
      if (!vendorsByCategoryId[v.vendorCategoryId]) vendorsByCategoryId[v.vendorCategoryId] = [];
      vendorsByCategoryId[v.vendorCategoryId].push(v);
    } else if (v.type === 'marketplace') {
      marketplaceVendors.push(v);
    } else {
      uncategorizedVendors.push(v);
    }
  });

  // ── Render ───────────────────────────────────────────────

  if (loading) return <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>;

  const sharedRowProps = {
    vendorCategories,
    onEdit: openEditDialog,
    onDelete: handleDeleteVendor,
    onToggleBudget: handleToggleBudget,
    onAssignCategory: handleAssignCategory,
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: 1.5 }}>
        <Alert severity="info" sx={{ flex: 1 }}>
          A piactérről foglalt szolgáltatók automatikusan megjelennek. Hozz létre kategóriákat, és adj hozzá egyedi szolgáltatókat!
        </Alert>
        <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
          <Button variant="outlined" startIcon={<CategoryIcon />} onClick={() => { setNewCategoryName(''); setAddCategoryOpen(true); }}>
            Új kategória
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => { resetForm(); setAddVendorOpen(true); }}>
            Szolgáltató
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {vendorCategories.length === 0 && marketplaceVendors.length === 0 && uncategorizedVendors.length === 0 ? (
        <Card sx={{ textAlign: 'center', py: 6, borderRadius: 3, border: (t) => `1px dashed ${t.palette.divider}` }} elevation={0}>
          <CategoryIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>Még nincs kategória</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Hozz létre kategóriákat (pl. Díszítés, Zene) és adj hozzá szolgáltatókat!
          </Typography>
          <Button variant="contained" startIcon={<CategoryIcon />} onClick={() => { setNewCategoryName(''); setAddCategoryOpen(true); }}>
            Első kategória létrehozása
          </Button>
        </Card>
      ) : (
        <Stack spacing={0}>
          {/* User-defined categories */}
          {vendorCategories.map((cat) => (
            <CategorySection
              key={cat.id}
              category={cat}
              vendors={vendorsByCategoryId[cat.id] || []}
              vendorCategories={vendorCategories}
              onAddVendor={(catId) => { setFormData({ ...EMPTY_FORM, vendorCategoryId: catId }); setAddVendorOpen(true); }}
              onEditVendor={openEditDialog}
              onDeleteVendor={handleDeleteVendor}
              onToggleBudget={handleToggleBudget}
              onAssignCategory={handleAssignCategory}
              onRenameCategory={(c) => { setSelectedCategory(c); setNewCategoryName(c.name); setNewCategoryBudget(c.plannedBudget != null ? String(Number(c.plannedBudget)) : ''); setRenameCategoryOpen(true); }}
              onDeleteCategory={handleDeleteCategory}
            />
          ))}

          {/* Uncategorized custom vendors */}
          {uncategorizedVendors.length > 0 && (
            <Accordion defaultExpanded disableGutters elevation={0}
              sx={{ border: (t) => `1px solid ${t.palette.divider}`, borderRadius: '10px !important', '&:before': { display: 'none' }, mb: 1.5 }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: 'grey.50', borderRadius: '10px', px: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', minWidth: 0 }}>
                  <InboxIcon fontSize="small" color="action" sx={{ flexShrink: 0 }} />
                  <Typography variant="subtitle1" fontWeight={700} noWrap sx={{ flex: 1, minWidth: 0 }}>Kategória nélkül</Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                {uncategorizedVendors.map((v, i) => (
                  <React.Fragment key={v.id}>
                    {i > 0 && <Divider />}
                    <VendorRow vendor={v} {...sharedRowProps} isMarketplace={false} />
                  </React.Fragment>
                ))}
              </AccordionDetails>
            </Accordion>
          )}

          {/* Marketplace vendors without category */}
          {marketplaceVendors.length > 0 && (
            <Accordion defaultExpanded disableGutters elevation={0}
              sx={{ border: (t) => `1px solid ${t.palette.divider}`, borderRadius: '10px !important', '&:before': { display: 'none' }, mb: 1.5 }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: 'primary.50', borderRadius: '10px', px: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', minWidth: 0 }}>
                  <StoreIcon fontSize="small" color="primary" sx={{ flexShrink: 0 }} />
                  <Typography variant="subtitle1" fontWeight={700} noWrap sx={{ flex: 1, minWidth: 0 }}>Piactéren foglalt</Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                {marketplaceVendors.map((v, i) => (
                  <React.Fragment key={v.id}>
                    {i > 0 && <Divider />}
                    <VendorRow vendor={v} {...sharedRowProps} isMarketplace />
                  </React.Fragment>
                ))}
              </AccordionDetails>
            </Accordion>
          )}
        </Stack>
      )}

      {/* ── Add / Edit Vendor Dialogs ── */}
      <VendorFormDialog
        open={addVendorOpen}
        title="Egyedi Szolgáltató Hozzáadása"
        formData={formData}
        onChange={setFormData}
        onClose={() => { setAddVendorOpen(false); resetForm(); }}
        onSubmit={handleAddVendor}
        submitLabel="Hozzáadás"
        vendorCategories={vendorCategories}
        isMobile={isMobile}
      />
      <VendorFormDialog
        open={editVendorOpen}
        title="Szolgáltató Szerkesztése"
        formData={formData}
        onChange={setFormData}
        onClose={() => { setEditVendorOpen(false); setSelectedVendor(null); resetForm(); }}
        onSubmit={handleEditVendor}
        submitLabel="Mentés"
        vendorCategories={vendorCategories}
        isMobile={isMobile}
      />

      {/* ── Add Category Dialog ── */}
      <Dialog open={addCategoryOpen} onClose={() => { setAddCategoryOpen(false); setNewCategoryName(''); setNewCategoryBudget(''); }} maxWidth="xs" fullWidth fullScreen={isMobile}>
        <DialogTitle sx={{ p: 0 }}>
          <DialogHeader icon={<CategoryIcon />} title="Új kategória" onClose={() => { setAddCategoryOpen(false); setNewCategoryName(''); setNewCategoryBudget(''); }} />
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              autoFocus
              label="Kategória neve *"
              fullWidth
              value={newCategoryName}
              onChange={(e) => setNewCategoryName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAddCategory()}
              placeholder="pl. Díszítés, Zene, Vendéglátás"
            />
            <TextField
              label="Tervezett keret"
              type="number"
              fullWidth
              value={newCategoryBudget}
              onChange={(e) => setNewCategoryBudget(e.target.value)}
              placeholder="0"
              slotProps={{ input: { endAdornment: <InputAdornment position="end">Ft</InputAdornment> } }}
              helperText="Ennyi összeget szánok erre a kategóriára"
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2.5, gap: 1, borderTop: '1px solid', borderTopColor: 'divider' }}>
          <Button onClick={() => { setAddCategoryOpen(false); setNewCategoryName(''); setNewCategoryBudget(''); }}>Mégse</Button>
          <Button variant="contained" onClick={handleAddCategory} disabled={!newCategoryName.trim()}>Létrehozás</Button>
        </DialogActions>
      </Dialog>

      {/* ── Rename / Edit Category Dialog ── */}
      <Dialog open={renameCategoryOpen} onClose={() => { setRenameCategoryOpen(false); setNewCategoryName(''); setNewCategoryBudget(''); }} maxWidth="xs" fullWidth fullScreen={isMobile}>
        <DialogTitle sx={{ p: 0 }}>
          <DialogHeader icon={<EditIcon />} title="Kategória szerkesztése" onClose={() => { setRenameCategoryOpen(false); setNewCategoryName(''); setNewCategoryBudget(''); }} />
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              autoFocus
              label="Kategória neve *"
              fullWidth
              value={newCategoryName}
              onChange={(e) => setNewCategoryName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleRenameCategory()}
            />
            <TextField
              label="Tervezett keret"
              type="number"
              fullWidth
              value={newCategoryBudget}
              onChange={(e) => setNewCategoryBudget(e.target.value)}
              placeholder="0"
              slotProps={{ input: { endAdornment: <InputAdornment position="end">Ft</InputAdornment> } }}
              helperText="Ennyi összeget szánok erre a kategóriára"
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2.5, gap: 1, borderTop: '1px solid', borderTopColor: 'divider' }}>
          <Button onClick={() => { setRenameCategoryOpen(false); setNewCategoryName(''); setNewCategoryBudget(''); }}>Mégse</Button>
          <Button variant="contained" onClick={handleRenameCategory} disabled={!newCategoryName.trim()}>Mentés</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default VendorsTab;
